/**
 * Client-side EU VAT-ID (USt-IdNr) format help + plausibility check.
 *
 * This is NOT the authoritative validation — VIES is (see validateVatId). Its jobs are:
 *  1. Give the member a country-aware example/placeholder BEFORE they type (e.g. DE123456789).
 *  2. Catch an obviously-wrong entry (a personal Steuer-IdNr, a Steuernummer, random digits)
 *     before we spend a VIES round-trip, and tell them the expected shape.
 *
 * `pattern` matches the VAT NUMBER (the part AFTER the 2-letter prefix); `example` is the full ID.
 * Greece's VAT prefix is `EL`, not its ISO code `GR` — handled by vatPrefixFor().
 */
interface VatFormat {
  pattern: RegExp
  example: string
}

const EU_VAT: Record<string, VatFormat> = {
  AT: {pattern: /^U\d{8}$/, example: 'ATU12345678'},
  BE: {pattern: /^0\d{9}$/, example: 'BE0123456789'},
  BG: {pattern: /^\d{9,10}$/, example: 'BG123456789'},
  CY: {pattern: /^\d{8}[A-Z]$/, example: 'CY12345678L'},
  CZ: {pattern: /^\d{8,10}$/, example: 'CZ12345678'},
  DE: {pattern: /^\d{9}$/, example: 'DE123456789'},
  DK: {pattern: /^\d{8}$/, example: 'DK12345678'},
  EE: {pattern: /^\d{9}$/, example: 'EE123456789'},
  EL: {pattern: /^\d{9}$/, example: 'EL123456789'}, // Greece — prefix EL, ISO GR
  ES: {pattern: /^[0-9A-Z]\d{7}[0-9A-Z]$/, example: 'ESX1234567X'},
  FI: {pattern: /^\d{8}$/, example: 'FI12345678'},
  FR: {pattern: /^[0-9A-Z]{2}\d{9}$/, example: 'FRXX123456789'},
  HR: {pattern: /^\d{11}$/, example: 'HR12345678901'},
  HU: {pattern: /^\d{8}$/, example: 'HU12345678'},
  IE: {pattern: /^(\d{7}[A-Z]{1,2}|\d[A-Z]\d{5}[A-Z])$/, example: 'IE1234567X'},
  IT: {pattern: /^\d{11}$/, example: 'IT12345678901'},
  LT: {pattern: /^(\d{9}|\d{12})$/, example: 'LT123456789'},
  LU: {pattern: /^\d{8}$/, example: 'LU12345678'},
  LV: {pattern: /^\d{11}$/, example: 'LV12345678901'},
  MT: {pattern: /^\d{8}$/, example: 'MT12345678'},
  NL: {pattern: /^\d{9}B\d{2}$/, example: 'NL123456789B01'},
  PL: {pattern: /^\d{10}$/, example: 'PL1234567890'},
  PT: {pattern: /^\d{9}$/, example: 'PT123456789'},
  RO: {pattern: /^\d{2,10}$/, example: 'RO1234567890'},
  SE: {pattern: /^\d{12}$/, example: 'SE123456789012'},
  SI: {pattern: /^\d{8}$/, example: 'SI12345678'},
  SK: {pattern: /^\d{10}$/, example: 'SK1234567890'},
}

const DEFAULT_EXAMPLE = EU_VAT.DE.example

/** The VAT prefix for an ISO country code (Greece's VAT prefix is EL, not GR). */
export function vatPrefixFor(countryCode: string): string {
  const cc = countryCode.toUpperCase()
  return cc === 'GR' ? 'EL' : cc
}

/** Upper-cased, with spaces / dots / dashes removed — the shape patterns are written against. */
export function normalizeVatId(raw: string): string {
  return (raw || '').replace(/[\s.\-]/g, '').toUpperCase()
}

/**
 * The ISO country code implied by a VAT ID's own 2-letter prefix (e.g. "DE123456789" -> "DE",
 * "EL123456789" -> "GR"), or null if it has no recognised EU VAT prefix. Lets the VIES check run
 * off the number itself when no billing country has been picked yet (signup enters the UST-ID before
 * the address). Greece's VAT prefix is EL; its ISO code is GR.
 */
export function countryCodeFromVatId(value: string): string | null {
  const m = normalizeVatId(value).match(/^([A-Z]{2})/)
  if (!m || !EU_VAT[m[1]]) return null
  return m[1] === 'EL' ? 'GR' : m[1]
}

/**
 * Is this country an EU member (i.e. it issues an EU VAT-ID / USt-IdNr that VIES can check)?
 * Keyed off the EU_VAT table so there's one source of truth. A non-EU country (CH, GB, AE, US, …)
 * has only a local tax id, not an EU VAT-ID — the field relabels to "Tax ID", drops the
 * country-code format hint, and skips VIES. `null`/unknown is treated as EU (the VAT-ID default)
 * so the signup flow, where the id is often typed before the address, still offers the VIES path.
 */
export function isEuVatCountry(countryCode: string | null | undefined): boolean {
  if (!countryCode) return true
  return !!EU_VAT[vatPrefixFor(countryCode)]
}

/** A full example USt-IdNr for the selected country (DE example as the fallback). */
export function vatExampleFor(countryCode: string | null | undefined): string {
  if (!countryCode) return DEFAULT_EXAMPLE
  return EU_VAT[vatPrefixFor(countryCode)]?.example ?? DEFAULT_EXAMPLE
}

/** Remove a leading country prefix (e.g. "DE123456789" -> "123456789") so VIES gets the number. */
export function stripVatPrefix(value: string, countryCode: string | null): string {
  const v = normalizeVatId(value)
  const prefix = countryCode ? vatPrefixFor(countryCode) : v.slice(0, 2)
  return prefix && v.startsWith(prefix) ? v.slice(prefix.length) : v
}

/**
 * Could `value` plausibly be the USt-IdNr for `countryCode`? Accepts the number with or without the
 * country prefix. Returns true for an unknown/unlisted country as long as the general shape holds
 * (prefix + 8-12 alphanumerics), so VIES — not this guard — has the final say there.
 */
export function looksLikeVatId(value: string, countryCode: string | null): boolean {
  const v = normalizeVatId(value)
  if (!v) return false
  const expectedPrefix = countryCode ? vatPrefixFor(countryCode) : null

  // If the value carries its own 2-letter prefix, it must match the selected country (when known).
  const m = v.match(/^([A-Z]{2})(.*)$/)
  const hasKnownPrefix = !!(m && EU_VAT[m[1]])
  if (hasKnownPrefix && expectedPrefix && m![1] !== expectedPrefix) return false

  const prefix = hasKnownPrefix ? m![1] : expectedPrefix
  const body = hasKnownPrefix ? m![2] : v
  if (prefix && EU_VAT[prefix]) return EU_VAT[prefix].pattern.test(body)

  // Unknown/unselected country: require a plausible generic EU shape and let VIES decide.
  return /^[A-Z]{2}[0-9A-Z]{8,12}$/.test(v)
}
